import { Fragment, useState } from "react";
import services from "./services";
import processes from "./processes";
import Select, { GroupBase, MultiValue } from "react-select";
import { MyDate, MyFormData, myAxios } from "../common/functions";
import { Form, Outlet, useLoaderData, useRouteLoaderData } from "react-router-dom";
import { AsyncFormSelect, SelectOption as AsyncSelectOption } from "../common/AsyncSelect";
import { useAuthStore, User } from "../main/authStore";
import { Link } from "react-router-dom";
import { InfoDataEntry, Settings, StatusEntry, WorkOrder, WorkOrderFormProps, assignmentText, workOrderStatusOptions } from "./types";

type ServiceOption = {
    label: string;
    value: string;
    optgroup?: string;
};

type ServiceGroup = {
    label: string;
    options: ServiceOption[];
};

type WorkOrderInfoDataEntry = InfoDataEntry & {
    value?: string;
};

type WorkOrderFormValues = Partial<WorkOrder> & {
    notes: string;
    holdReason: string;
    acceptedQuantity: string;
    rejectedQuantity: string;
    reportNotes: string;
    inspectorInitials: string;
    labNumberDate: string;
    poNumber: string;
    customer: string;
    requestor: string;
    receivedById: string;
    receivedByName: string;
    workOrderStatus: WorkOrder["workOrderStatus"];
    deadline: string;
    labNumber: string;
    partsReceivedDate: string;
    workOrderDate: string;
    quoteNumber: string;
    techs: AsyncSelectOption[];
    statusData: Record<string, Record<string, StatusEntry>>;
    infoData: WorkOrderInfoDataEntry[];
    organizationId?: string;
    organizationName?: string;
    contactId?: string;
    contactName?: string;
};

const serviceGroups = services as ServiceGroup[];
const normalizedServiceGroups: GroupBase<WorkOrderInfoDataEntry>[] = serviceGroups.map(group => ({
    label: group.label,
    options: group.options.map(option => ({
        ...option,
        optgroup: option.optgroup ?? group.label
    }))
}));

export default function WorkOrderForm({ keyword }: WorkOrderFormProps) {
    const user = useAuthStore(state => state.user) as User;
    const settings = useLoaderData() as Settings | undefined;
    const defaultReceivedByName = [user.firstName, user.middleName, user.lastName].filter(Boolean).join(" ") || user.userName || "";
    const defaultFormValues: WorkOrderFormValues = {
        notes: "",
        holdReason: "",
        acceptedQuantity: "",
        rejectedQuantity: "",
        reportNotes: "",
        inspectorInitials: "",
        labNumberDate: "",
        poNumber: "",
        customer: "",
        requestor: "",
        receivedById: user.id ?? "",
        receivedByName: defaultReceivedByName,
        workOrderStatus: "Queue",
        deadline: "",
        labNumber: "",
        partsReceivedDate: new MyDate().s(),
        workOrderDate: "",
        quoteNumber: "",
        techs: [],
        statusData: Object.fromEntries(normalizedServiceGroups.map(v => [v.label, {}])) as Record<string, Record<string, StatusEntry>>,
        infoData: []
    };

    const routeData = useRouteLoaderData("workOrder") as WorkOrder | undefined;
    const initialValues: WorkOrderFormValues = routeData
        ? {
              ...defaultFormValues,
              ...routeData,
              holdReason: routeData.holdReason ?? "",
              acceptedQuantity: routeData.acceptedQuantity !== undefined && routeData.acceptedQuantity !== null ? String(routeData.acceptedQuantity) : "",
              rejectedQuantity: routeData.rejectedQuantity !== undefined && routeData.rejectedQuantity !== null ? String(routeData.rejectedQuantity) : "",
              reportNotes: routeData.reportNotes ?? "",
              inspectorInitials: routeData.inspectorInitials ?? "",
              techs: routeData.techs ?? [],
              statusData: routeData.statusData ?? defaultFormValues.statusData,
              infoData: (routeData.infoData as WorkOrderInfoDataEntry[]) ?? []
          }
        : defaultFormValues;
    const [formValues, setFormValues] = useState<WorkOrderFormValues>(initialValues);
    const [poInvalid, setPoInvalid] = useState("");
    const [curPoNumber, setPoNumber] = useState(formValues.poNumber ?? "");
    const [currentWorkOrderStatus, setCurrentWorkOrderStatus] = useState<WorkOrder["workOrderStatus"]>(formValues.workOrderStatus ?? "Queue");
    const [holdReason, setHoldReason] = useState(formValues.holdReason ?? "");
    const [acceptedQuantity, setAcceptedQuantity] = useState(String(formValues.acceptedQuantity ?? ""));
    const [rejectedQuantity, setRejectedQuantity] = useState(String(formValues.rejectedQuantity ?? ""));
    const [reportNotes, setReportNotes] = useState(formValues.reportNotes ?? "");
    const [inspectorInitials, setInspectorInitials] = useState(formValues.inspectorInitials ?? "");
    const [curInfoData, setInfoData] = useState<WorkOrderInfoDataEntry[]>(formValues.infoData ?? []);
    const [curStatusData, setStatusData] = useState<Record<string, Record<string, StatusEntry>>>(formValues.statusData ?? {});
    const technicianOptions = formValues.techs ?? [];
    const totalRequestedQuantity = curInfoData.reduce((total, item) => {
        const qty = String(item.qty ?? "").trim();
        return /^\d+$/.test(qty) ? total + Number(qty) : total;
    }, 0);

    return (
        <Form method="post">
            <input type="hidden" readOnly name="id" value={JSON.stringify(formValues.id ?? null)} />
            <input type="hidden" readOnly name="infoData" value={JSON.stringify(curInfoData)} />
            <input type="hidden" readOnly name="statusData" value={JSON.stringify(curStatusData)} />
            <input type="hidden" readOnly name="action" value={keyword} />
            <input type="hidden" readOnly name="poInvalid" value={poInvalid} />
            <input type="hidden" readOnly name="holdReason" value={currentWorkOrderStatus === "Hold" ? holdReason : ""} />
            <input type="hidden" readOnly name="acceptedQuantity" value={currentWorkOrderStatus === "Report Assist" ? acceptedQuantity : ""} />
            <input type="hidden" readOnly name="rejectedQuantity" value={currentWorkOrderStatus === "Report Assist" ? rejectedQuantity : ""} />
            <input type="hidden" readOnly name="reportNotes" value={currentWorkOrderStatus === "Report Assist" ? reportNotes : ""} />
            <input type="hidden" readOnly name="inspectorInitials" value={currentWorkOrderStatus === "Report Assist" ? inspectorInitials : ""} />
            <div style={{ color: "red", textAlign: "center", margin: "1em", fontWeight: 650, fontSize: "2em" }}>
                Work Order Form
            </div>
            {formValues.labNumber ? (
                <div className="my-4 text-right">
                    <label className="fw-bold" htmlFor="labNumber">
                        Lab Number:
                    </label>
                    <input className="form-control fw-bold h5" id="labNumber" name="labNumber" readOnly value={formValues.labNumber ?? ""} />
                </div>
            ) : null}
            <div className="my-4">
                <label className="fw-bold required-field" htmlFor="techs">
                    Technicians:
                </label>
                <AsyncFormSelect
                    className="flex-grow-1"
                    placeholder="Search user…"
                    required={true}
                    isMulti={true}
                    name={"techs"}
                    inputId={"techs"}
                    fileName="common/searchUser.php"
                    value={formValues.techs.length ? formValues.techs : null}
                    onChange={next =>
                        setFormValues({
                            ...formValues,
                            techs: ((next as MultiValue<AsyncSelectOption> | null) ?? []).map(v => ({
                                value: v.value,
                                label: v.label
                            }))
                        })
                    }
                />
            </div>
            <div className="my-4">
                <label className="fw-bold required-field" htmlFor="labNumberDate">
                    Lab Number Date:
                </label>
                <input required type="date" className="form-control" id="labNumberDate" name="labNumberDate" defaultValue={formValues.labNumberDate || ""} />
            </div>
            <div className="my-4">
                <label className="fw-bold required-field" htmlFor="partsReceivedDate">
                    Parts Received Date:
                </label>
                <input required type="date" className="form-control" id="partsReceivedDate" name="partsReceivedDate" defaultValue={formValues.partsReceivedDate || ""} />
            </div>
            <div className="my-4">
                <label className="fw-bold required-field text-nowrap" htmlFor="receivedById">
                    Received By:
                </label>
                <AsyncFormSelect
                    className="flex-grow-1"
                    placeholder="Search user…"
                    required={true}
                    name={"receivedById"}
                    inputId={"receivedById"}
                    fileName="common/searchUser.php"
                    value={
                        formValues.receivedById
                            ? {
                                  label: formValues.receivedByName ?? "",
                                  value: formValues.receivedById
                              }
                            : null
                    }
                    onChange={next => {
                        const option = next as AsyncSelectOption | null;
                        setFormValues({
                            ...formValues,
                            receivedById: option?.value ?? "",
                            receivedByName: option?.label ?? ""
                        });
                    }}
                />
            </div>
            <div className="my-4">
                <label className="fw-bold required-field" htmlFor="workOrderDate">
                    Work Order Created:
                </label>
                <input required type="date" className="form-control" id="workOrderDate" name="workOrderDate" defaultValue={formValues.workOrderDate || ""} />
            </div>
            <div className="my-4">
                <label className="fw-bold text-nowrap required-field" htmlFor="organizationId">
                    Organization Name:
                </label>
                <AsyncFormSelect
                    className="flex-grow-1"
                    placeholder="Search organization…"
                    required={true}
                    name={"organizationId"}
                    inputId={"organizationId"}
                    fileName="common/searchOrganization.php"
                    value={
                        formValues.organizationId
                            ? {
                                  label: formValues.organizationName ?? "",
                                  value: formValues.organizationId
                              }
                            : null
                    }
                    onChange={next => {
                        const option = next as AsyncSelectOption | null;
                        setFormValues({
                            ...formValues,
                            organizationId: option?.value ?? "",
                            organizationName: option?.label ?? "",
                            contactId: "",
                            contactName: ""
                        });
                    }}
                />
            </div>
            <div className="my-4">
                <label className="fw-bold text-nowrap required-field" htmlFor="contactId">
                    Contact Name:
                </label>
                <AsyncFormSelect
                    className="flex-grow-1"
                    placeholder="Search contact…"
                    required={true}
                    name={"contactId"}
                    inputId={"contactId"}
                    fileName="common/searchContactInOrganization.php"
                    value={
                        formValues.contactId
                            ? {
                                  label: formValues.contactName ?? "",
                                  value: formValues.contactId
                              }
                            : null
                    }
                    onChange={next => {
                        const option = next as AsyncSelectOption | null;
                        setFormValues({
                            ...formValues,
                            contactId: option?.value ?? "",
                            contactName: option?.label ?? ""
                        });
                    }}
                    formValues={{ organizationId: formValues.organizationId ?? "" }}
                />
            </div>
            <div className="my-4">
                <label className="fw-bold" htmlFor="poNumber">
                    PO Number:
                </label>
                <input
                    type="text"
                    className="form-control"
                    id="poNumber"
                    name="poNumber"
                    value={curPoNumber}
                    onChange={e => setPoNumber(e.currentTarget.value.replace(/\//g, "").replace(/ /g, ""))}
                    onBlur={async e => {
                        const target = e.currentTarget.value.replace(/\//g, "").replace(/ /g, "");
                        if (target === (formValues.poNumber ?? "")) {
                            return;
                        }
                        if (!target) {
                            setPoInvalid("");
                            return;
                        }
                        const formData = new MyFormData();
                        formData.append("poNumber", target);
                        const res = await myAxios({ fileName: "workOrders/checkDuplicatePO.php", formData });
                        if (!res) return;
                        setPoInvalid(res.data);
                    }}
                />
                {poInvalid ? <span className={"mx-3 text-danger fw-bold"}>duplicated with {poInvalid}</span> : null}
            </div>
            <div className="my-4">
                <label className="fw-bold" htmlFor="quoteNumber">
                    Quote Number:
                </label>
                <input type="text" className="form-control" id="quoteNumber" name="quoteNumber" defaultValue={formValues.quoteNumber || ""} />
            </div>
            <div className="my-4">
                <label className="fw-bold required-field" htmlFor="deadline">
                    Deadline:
                </label>
                <input required type="date" className="form-control" id="deadline" name="deadline" defaultValue={formValues.deadline || ""} />
            </div>
            <div className="my-4">
                <label className="fw-bold required-field" htmlFor="workOrderStatus">
                    Work Order Status:
                </label>
                <select
                    required
                    className="form-control"
                    id="workOrderStatus"
                    name="workOrderStatus"
                    value={currentWorkOrderStatus}
                    onChange={e => setCurrentWorkOrderStatus(e.currentTarget.value as WorkOrder["workOrderStatus"])}
                >
                    {workOrderStatusOptions.map(option => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </select>
            </div>
            {currentWorkOrderStatus === "Hold" ? (
                <div className="my-4">
                    <label className="fw-bold required-field" htmlFor="holdReason">
                        Hold Reason:
                    </label>
                    <input
                        id="holdReason"
                        className="form-control"
                        required
                        maxLength={120}
                        placeholder="Short reason"
                        value={holdReason}
                        onChange={e => setHoldReason(e.currentTarget.value)}
                    />
                </div>
            ) : null}
            {currentWorkOrderStatus === "Report Assist" ? (
                <>
                    <div className="my-4">
                        <label className="fw-bold required-field" htmlFor="acceptedQuantity">
                            Accepted Quantity:
                        </label>
                        <input
                            id="acceptedQuantity"
                            className="form-control"
                            type="number"
                            min={0}
                            step={1}
                            required
                            value={acceptedQuantity}
                            onChange={e => setAcceptedQuantity(e.currentTarget.value)}
                            max={totalRequestedQuantity || undefined}
                        />
                    </div>
                    <div className="my-4">
                        <label className="fw-bold required-field" htmlFor="rejectedQuantity">
                            Rejected Quantity:
                        </label>
                        <input
                            id="rejectedQuantity"
                            className="form-control"
                            type="number"
                            min={0}
                            step={1}
                            required
                            value={rejectedQuantity}
                            onChange={e => setRejectedQuantity(e.currentTarget.value)}
                            max={totalRequestedQuantity || undefined}
                        />
                        <div className="form-text">
                            Total accepted + rejected must be less than or equal to total quantity ({totalRequestedQuantity}).
                        </div>
                    </div>
                    <div className="my-4">
                        <label className="fw-bold required-field" htmlFor="reportNotes">
                            Report Notes:
                        </label>
                        <textarea
                            id="reportNotes"
                            className="form-control"
                            required
                            value={reportNotes}
                            onChange={e => setReportNotes(e.currentTarget.value)}
                        />
                    </div>
                    <div className="my-4">
                        <label className="fw-bold required-field" htmlFor="inspectorInitials">
                            Inspector Initials:
                        </label>
                        <select
                            id="inspectorInitials"
                            className="form-control"
                            required
                            value={inspectorInitials}
                            onChange={e => setInspectorInitials(e.currentTarget.value)}
                        >
                            <option value="">Select</option>
                            {technicianOptions.map(tech => (
                                <option key={tech.value} value={tech.label}>
                                    {tech.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </>
            ) : null}
            <div className="my-4">
                <label className="fw-bold" htmlFor="assignedStatus">
                    Assigned:
                </label>
                <input
                    className="form-control"
                    id="assignedStatus"
                    readOnly
                    value={assignmentText(formValues.techs)}
                />
            </div>
            <h4 className="fw-bold text-center">Contract Review</h4>
            <label className="fw-bold" htmlFor="testRequirements">
                TEST REQUIREMENT(S)
            </label>
            <Select<WorkOrderInfoDataEntry, true>
                className="mb-4"
                inputId="testRequirements"
                isMulti
                onChange={selected => {
                    const newCurInfoData = selected.map(option => {
                        const existing = curInfoData.find(v => v.label === option.label);
                        if (existing) return existing;
                        return {
                            ...option,
                            optgroup: option.optgroup ?? ""
                        };
                    });

                    setInfoData(newCurInfoData);
                    const uniqueGroup = Array.from(new Set(newCurInfoData.map(v => v.optgroup).filter(Boolean))) as string[];

                    const obj = Object.fromEntries(
                        uniqueGroup.map(group => [
                            group,
                            Object.fromEntries(
                                processes({ label: group }, newCurInfoData).map(proc => [proc, curStatusData[group]?.[proc] ?? { complete: false }])
                            )
                        ])
                    ) as Record<string, Record<string, StatusEntry>>;
                    setStatusData(obj);
                }}
                defaultValue={curInfoData}
                options={normalizedServiceGroups}
                getOptionLabel={option => option.label}
                getOptionValue={option => option.value ?? option.label}
            />
            {curInfoData.length ? (
                <div className="table-responsive">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>TEST REQUIREMENT</th>
                                {curInfoData.map((v, i) => (
                                    <th key={i}>{v.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <th>Part Number</th>
                                {curInfoData.map((v, i) => (
                                    <th key={i}>
                                        <input
                                            className="form-control"
                                            value={v.pn || ""}
                                            onChange={e => {
                                                setInfoData(curInfoData.map((vv, ii) => (ii === i ? { ...vv, pn: e.currentTarget.value } : vv)));
                                            }}
                                        />
                                    </th>
                                ))}
                            </tr>
                            <tr>
                                <th>Quantity</th>
                                {curInfoData.map((v, i) => (
                                    <th key={i}>
                                        <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            className="form-control"
                                            value={v.qty || ""}
                                            onChange={e => {
                                                setInfoData(curInfoData.map((vv, ii) => (ii === i ? { ...vv, qty: e.currentTarget.value } : vv)));
                                            }}
                                        />
                                    </th>
                                ))}
                            </tr>
                            <tr>
                                <th>Process</th>
                                {curInfoData.map((v, i) => (
                                    <th key={i}>
                                        <input
                                            className="form-control"
                                            value={v.proc || ""}
                                            onChange={e => {
                                                setInfoData(curInfoData.map((vv, ii) => (ii === i ? { ...vv, proc: e.currentTarget.value } : vv)));
                                            }}
                                        />
                                    </th>
                                ))}
                            </tr>
                            <tr>
                                <th>Criteria</th>
                                {curInfoData.map((v, i) => (
                                    <th key={i}>
                                        <input
                                            className="form-control"
                                            value={v.criteria || ""}
                                            onChange={e => {
                                                setInfoData(curInfoData.map((vv, ii) => (ii === i ? { ...vv, criteria: e.currentTarget.value } : vv)));
                                            }}
                                        />
                                    </th>
                                ))}
                            </tr>
                            <tr>
                                <th>BMTI Technician</th>
                                {curInfoData.map((v, i) => (
                                    <th key={i}>
                                        <input
                                            className="form-control"
                                            value={v.tech || ""}
                                            onChange={e => {
                                                setInfoData(curInfoData.map((vv, ii) => (ii === i ? { ...vv, tech: e.currentTarget.value } : vv)));
                                            }}
                                        />
                                    </th>
                                ))}
                            </tr>
                        </tbody>
                    </table>
                </div>
            ) : null}
            {serviceGroups.map((a, i) =>
                curInfoData.filter(v => v.optgroup === a.label).length > 0 ? (
                    <Fragment key={i}>
                        <div className="fw-bold text-center h4">{a.label}</div>
                        <div className=" table-responsive">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Step</th>
                                        <th className="text-wrap">BMTI Process</th>
                                        <th>Quantity</th>
                                        <th>ETA</th>
                                        <th>Duration</th>
                                        <th>Notes</th>
                                        <th className="text-wrap">Inspector Signature</th>
                                        <th className="text-wrap">Optional Inspectors</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {processes(a, curInfoData).map((r, ri) => (
                                        <tr key={ri}>
                                            <th className="text-success">{r}</th>
                                            <td>
                                                <select
                                                    className="form-control"
                                                    required={Boolean(curStatusData[a.label]?.[r]?.initial)}
                                                    value={curStatusData[a.label]?.[r]?.proc || ""}
                                                    onChange={e => {
                                                        setStatusData({
                                                            ...curStatusData,
                                                            [a.label]: {
                                                                ...curStatusData[a.label],
                                                                [r]: {
                                                                    ...curStatusData[a.label]?.[r],
                                                                    proc: e.currentTarget.value
                                                                }
                                                            }
                                                        });
                                                    }}
                                                >
                                                    <option value="">Select</option>
                                                    {settings?.proc.map((p, key) => (
                                                        <optgroup key={key} label={p.category}>
                                                            {p.data.map((d, k) => (
                                                                <option key={k}>{d.proc}</option>
                                                            ))}
                                                        </optgroup>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>
                                                <input
                                                    required={Boolean(curStatusData[a.label]?.[r]?.userId)}
                                                    className="form-control"
                                                    type="number"
                                                    style={{ width: 100 }}
                                                    min = {0}
                                                    value={curStatusData[a.label]?.[r]?.qty || ""}
                                                    onChange={e => {
                                                        setStatusData({
                                                            ...curStatusData,
                                                            [a.label]: {
                                                                ...curStatusData[a.label],
                                                                [r]: {
                                                                    ...curStatusData[a.label]?.[r],
                                                                    qty: e.currentTarget.value
                                                                }
                                                            }
                                                        });
                                                    }}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    required={Boolean(curStatusData[a.label]?.[r]?.userId)}
                                                    className="form-control"
                                                    type="number"
                                                    style={{ width: 100 }}
                                                    step={0.1}
                                                    min = {0}
                                                    value={curStatusData[a.label]?.[r]?.eta || ""}
                                                    onChange={e => {
                                                        setStatusData({
                                                            ...curStatusData,
                                                            [a.label]: {
                                                                ...curStatusData[a.label],
                                                                [r]: {
                                                                    ...curStatusData[a.label]?.[r],
                                                                    eta: e.currentTarget.value
                                                                }
                                                            }
                                                        });
                                                    }}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    required={Boolean(curStatusData[a.label]?.[r]?.userId)}
                                                    className="form-control"
                                                    type="number"
                                                    style={{ width: 100 }}
                                                    step={0.1}
                                                    min = {0}
                                                    value={curStatusData[a.label]?.[r]?.duration ?? "0"}
                                                    onChange={e => {
                                                        setStatusData({
                                                            ...curStatusData,
                                                            [a.label]: {
                                                                ...curStatusData[a.label],
                                                                [r]: {
                                                                    ...curStatusData[a.label]?.[r],
                                                                    duration: e.currentTarget.value
                                                                }
                                                            }
                                                        });
                                                    }}
                                                />
                                            </td>
                                            <td>
                                                <textarea
                                                    className="form-control"
                                                    value={curStatusData[a.label]?.[r]?.notes ?? ""}
                                                    onChange={e => {
                                                        setStatusData({
                                                            ...curStatusData,
                                                            [a.label]: {
                                                                ...curStatusData[a.label],
                                                                [r]: {
                                                                    ...curStatusData[a.label]?.[r],
                                                                    notes: e.currentTarget.value
                                                                }
                                                            }
                                                        });
                                                    }}
                                                />
                                            </td>
                                            <td>
                                                <select
                                                    className="form-control"
                                                    required={
                                                        Boolean(curStatusData[a.label]?.[r]?.proc) ||
                                                        Boolean(curStatusData[a.label]?.[r]?.qty) ||
                                                        Boolean(curStatusData[a.label]?.[r]?.eta) ||
                                                        Boolean(curStatusData[a.label]?.[r]?.duration) ||
                                                        Boolean(curStatusData[a.label]?.[r]?.notes)
                                                    }
                                                    value={curStatusData[a.label]?.[r]?.userId ?? ""}
                                                    onChange={e => {
                                                        const nextUserId = e.currentTarget.value;
                                                        const selectedTech = technicianOptions.find(tech => tech.value === nextUserId);
                                                        setStatusData({
                                                            ...curStatusData,
                                                            [a.label]: {
                                                                ...curStatusData[a.label],
                                                                [r]: {
                                                                    ...curStatusData[a.label]?.[r],
                                                                    userId: nextUserId,
                                                                    initial: selectedTech?.label ?? ""
                                                                }
                                                            }
                                                        });
                                                    }}
                                                >
                                                    <option value="">Select</option>
                                                    {technicianOptions.map(tech => (
                                                        <option key={tech.value} value={tech.value}>
                                                            {tech.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>
                                                <Select<AsyncSelectOption, true>
                                                    className="flex-grow-1"
                                                    isMulti
                                                    closeMenuOnSelect={false}
                                                    options={technicianOptions}
                                                    value={technicianOptions.filter(tech =>
                                                        (curStatusData[a.label]?.[r]?.optionalInspectors ?? []).includes(tech.value)
                                                    )}
                                                    onChange={selected => {
                                                        const nextOptionalInspectors = selected.map(option => option.value);
                                                        setStatusData({
                                                            ...curStatusData,
                                                            [a.label]: {
                                                                ...curStatusData[a.label],
                                                                [r]: {
                                                                    ...curStatusData[a.label]?.[r],
                                                                    optionalInspectors: nextOptionalInspectors
                                                                }
                                                            }
                                                        });
                                                    }}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Fragment>
                ) : null
            )}
            <div style={{ width: "100%", margin: "auto" }}>
                <label style={{ fontWeight: 650 }} htmlFor="notes">
                    Notes:
                </label>
                <textarea id="notes" name="notes" defaultValue={formValues.notes || ""} className="form-control mb-3" style={{ width: "100%" }} />
            </div>
            <div className="mb-3">
                <button
                    children = {keyword}
                    type = "submit"
                    className = "btn btn-primary"
                />
                <Link
                    children = "Back"
                    to = ".."
                    className = "btn btn-outline-secondary float-end"
                />
            </div>
            <Outlet/>
        </Form>
    );
}
